import React, { useEffect, useState } from 'react';
import DrawerModal from '../../../../components/drawer-modal';
import { HistoryBehavioralDrawerSC } from './history-behavioral-drawer.styled';
import { HistoryBehavioralDrawerData, IDateRange } from '../../../../types/life-id';
import { DEFAULT_DATE_RANGE, HistoryRangePicker } from '../history-range-picker';
import BehavioralChart from '../behavioral-chart';
import { connect } from 'react-redux';
import { actions } from '../../../../store/ducks/life-id';
import TStore from 'types/store';
import {
  IBehavioralTimelineData,
  TBehavioralTimelineParams,
  TBehavioralTimelineSleepItem,
  TBehavioralTimelineStepsItem,
} from 'types/behavioral-data';
import moment from 'moment';
import StepsListItem from '../behavioral-list/steps-list-item';
import { getTimeIntervalForCall } from '../../../../helpers/behavioral-helpers';
import { FormattedMessage } from 'react-intl';

interface HistoryBehavioralDrawerProps {
  userId: string;
  visible: boolean;
  onClose(): void;
  drawerData: HistoryBehavioralDrawerData;
  getBehavioralDataTimelineById(id: string, type: string, params: TBehavioralTimelineParams): void;
  behavioralDataTimeline: IBehavioralTimelineData | null;
}

const HistoryBehavioralDrawer: React.FC<HistoryBehavioralDrawerProps> = ({
  visible,
  onClose,
  drawerData,
  userId,
  behavioralDataTimeline,
  getBehavioralDataTimelineById,
}) => {
  const type = drawerData.type;

  const [selectedPeriod, setSelectedPeriod] = useState<string>('week');
  const [historyDate, setHistoryDate] = useState<string | string[]>('');
  const [historyCount, setHistoryCount] = useState<number>(0);
  const [dateRange, setDateRange] = useState<IDateRange>(DEFAULT_DATE_RANGE);
  const [params, setParams] = useState({
    step: 'day',
    dateFrom: moment().subtract(Math.abs(historyCount), 'week').startOf('week').toISOString(),
    dateTo: moment().subtract(Math.abs(historyCount), 'week').endOf('week').toISOString(),
  });
  const [isCustomTimeSelected, setCustomTimeIsSelected] = useState(false);

  const renderListItem = (
    type: string,
    item: TBehavioralTimelineStepsItem | TBehavioralTimelineSleepItem,
    index: number,
  ) => {
    switch (type) {
      case 'STEPS':
        const stepsItem = item as TBehavioralTimelineStepsItem;
        return (
          <StepsListItem
            key={index}
            steps={Math.round(stepsItem.value)}
            goal={stepsItem.goal}
            date={stepsItem.timestampTo}
            period={selectedPeriod}
          />
        );
    }
  };

  const onHistoryStepChange = (historyType: string) => {
    let actualCount = historyCount;
    if (historyType && historyType === 'next') {
      if (actualCount < 0) {
        setHistoryCount((actualCount += 1));
      }
    } else if (historyType === 'back') {
      setHistoryCount((actualCount -= 1));
    }
  };

  useEffect(() => {
    setHistoryCount(0);
  }, [selectedPeriod]);

  useEffect(() => {
    if (historyCount <= 0) {
      setHistoryDate([
        moment()
          .subtract(Math.abs(historyCount), selectedPeriod as moment.DurationInputArg2)
          .startOf(selectedPeriod as moment.DurationInputArg2)
          .toISOString(),
        moment()
          .subtract(Math.abs(historyCount), selectedPeriod as moment.DurationInputArg2)
          .endOf(selectedPeriod as moment.DurationInputArg2)
          .toISOString(),
      ]);
    } else {
      return;
    }
  }, [historyCount, selectedPeriod]);

  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      setParams({
        step: 'day',
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
      });
      setSelectedPeriod('month');
      setCustomTimeIsSelected(true);
    } else {
      setParams({
        step: getTimeIntervalForCall(selectedPeriod) || 'day',
        dateFrom: historyDate[0],
        dateTo: historyDate[1],
      });
      setCustomTimeIsSelected(false);
    }
  }, [dateRange, historyDate]);

  useEffect(() => {
    if (params && params.dateFrom && params.dateTo) {
      getBehavioralDataTimelineById(userId, type.toLocaleLowerCase(), params);
    }
  }, [params]);

  return (
    <DrawerModal visible={visible} onClose={onClose} closeTheme="LIGHT">
      <HistoryBehavioralDrawerSC>
        <div className="hbd-header">
          <BehavioralChart
            onHistoryStepChange={(historyType: string) => onHistoryStepChange(historyType)}
            historyDate={historyDate}
            historyCount={historyCount}
            type={type}
            data={behavioralDataTimeline}
            selectedPeriod={selectedPeriod}
            onPeriodChange={(value) => setSelectedPeriod(value)}
            isCustomTimeSelected={isCustomTimeSelected}
          />
        </div>
        {selectedPeriod === 'day' ? (
          ''
        ) : (
          <div className="hbd-date-picker">
            <HistoryRangePicker
              onSubmit={(args) => {
                setDateRange(args);
              }}
            />
            {behavioralDataTimeline?.values && behavioralDataTimeline?.values?.length > 0 && (
              <span className="hbd-date-picker__counter">
                <FormattedMessage
                  id="vital_history.n_records"
                  values={{ n: behavioralDataTimeline?.values?.length }}
                />
              </span>
            )}
          </div>
        )}

        <div className="hbd-body">
          <div className="hbd-body-content">
            <div className="hbd-body-list">
              {behavioralDataTimeline &&
                behavioralDataTimeline?.values.map((item, index) => {
                  return renderListItem(type, item, index);
                })}
            </div>
          </div>
        </div>
      </HistoryBehavioralDrawerSC>
    </DrawerModal>
  );
};

const mapStateToProps = (store: TStore) => ({
  behavioralDataTimeline: store.lifeId.behavioralDataTimeline,
});

const mapDispatchToProps = (dispatch: any) => ({
  getBehavioralDataTimelineById: (id: string, type: string, params: any) =>
    dispatch(actions.getBehavioralTimelineDataByIdRequest({ id, type, params })),
});

export default connect(mapStateToProps, mapDispatchToProps)(HistoryBehavioralDrawer);
